import React, { useEffect, useState } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faCircleCheck, faCircleXmark, faEdit, faSave, faTimes, faTags, faPlus, faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { getLabelColor } from '../../utils/labelColors';
import { UiPageHeader, UiModal, UiButton, StatusBadge, FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../../components/ui';
import type { StatusDef } from '../../components/ui';

interface MerchantWithStats {
  id: string;
  email: string;
  name: string;
  company_name: string;
  phone: string;
  is_active: boolean;
  fee_percentage: number;
  payout_fee_percentage: number;
  p2p_fee_payin_pct: number | null;
  p2p_fee_payout_pct: number | null;
  created_at: string;
  reseller_labels: string[];
  stats: {
    total_orders: number;
    total_paid_amount: number;
    total_fee: number;
    commission_rate: number;
    total_commission: number;
    total_payouts: number;
    total_payout_amount: number;
    payout_commission_rate: number;
    total_payout_commission: number;
  };
}

const MERCHANT_STATUS_MAP: Record<string, StatusDef> = {
  active: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCircleCheck, label: 'Active' },
  inactive: { bg: 'bg-red-100 dark:bg-red-500/15', text: 'text-red-700 dark:text-red-400', icon: faCircleXmark, label: 'Inactive' },
};

// input ขนาดกะทัดรัดสำหรับ inline fee editor (สไตล์เดียวกับ FIELD_INPUT_CLASS แต่ไม่บังคับ w-full)
const COMPACT_INPUT_CLASS =
  'px-2 py-1 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-200 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 placeholder:text-gray-400 dark:placeholder:text-slate-500';

const GHOST_BTN_CLASS =
  'px-2 py-1 text-xs text-gray-400 dark:text-slate-500 hover:text-primary hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors';

const ResellerMerchantsPage: React.FC = () => {
  const [merchants, setMerchants] = useState<MerchantWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFee, setEditFee] = useState('');
  const [editPayoutFee, setEditPayoutFee] = useState('');
  const [editP2pFee, setEditP2pFee] = useState('');
  const [editP2pPayoutFee, setEditP2pPayoutFee] = useState('');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingLabelsId, setEditingLabelsId] = useState<string | null>(null);
  const [editingLabels, setEditingLabels] = useState<string[]>([]);
  const [labelInput, setLabelInput] = useState('');
  const [savingLabels, setSavingLabels] = useState(false);

  const [changingPasswordId, setChangingPasswordId] = useState<string | null>(null);
  const [passwordData, setPasswordData] = useState({ new_password: '', confirm_password: '' });
  const [showPasswords, setShowPasswords] = useState({ new_password: false, confirm: false });
  const [savingPassword, setSavingPassword] = useState(false);

  const resellerUser = JSON.parse(localStorage.getItem('reseller_user') || '{}');

  useEffect(() => {
    loadMerchants();
  }, []);

  const loadMerchants = async () => {
    try {
      const response = await merchantApi.get('/reseller/merchants');
      setMerchants(response.data.data || []);
    } catch (error: any) {
      // เคลียร์ของเก่า ไม่ให้รายชื่อ/สถิติค้างเมื่อโหลดพลาด
      console.error('Error loading merchants:', error);
      setMerchants([]);
      toast.error('โหลดรายชื่อ Merchant ไม่สำเร็จ: ' + (error?.response?.data?.message || error?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (merchant: MerchantWithStats) => {
    setEditingId(merchant.id);
    setEditFee(String(merchant.fee_percentage || 0));
    setEditPayoutFee(String(merchant.payout_fee_percentage || 0));
    setEditP2pFee(merchant.p2p_fee_payin_pct != null ? String(merchant.p2p_fee_payin_pct) : '');
    setEditP2pPayoutFee(merchant.p2p_fee_payout_pct != null ? String(merchant.p2p_fee_payout_pct) : '');
    setEditCompanyName(merchant.company_name || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveFee = async (merchantId: string) => {
    const minPayinRate = parseFloat(resellerUser.fee_rate || 0);
    const minPayoutRate = parseFloat(resellerUser.payout_fee_rate || 0);
    // ⭐ ต้นทุน P2P effective (ไม่ตั้ง = ใช้ rate ปกติ) — backend บังคับ floor จริงอีกชั้น
    const minP2pRate = parseFloat((resellerUser.p2p_fee_rate ?? resellerUser.fee_rate) || 0);
    const minP2pPayoutRate = parseFloat((resellerUser.p2p_payout_fee_rate ?? resellerUser.payout_fee_rate) || 0);
    const feeVal = parseFloat(editFee);
    const payoutFeeVal = parseFloat(editPayoutFee);

    if (feeVal < minPayinRate) {
      toast.error(`Payin Fee ต้องไม่น้อยกว่า ${minPayinRate}% (rate ของคุณ)`);
      return;
    }
    if (payoutFeeVal < minPayoutRate) {
      toast.error(`Payout Fee ต้องไม่น้อยกว่า ${minPayoutRate}% (payout rate ของคุณ)`);
      return;
    }
    if (editP2pFee !== '' && parseFloat(editP2pFee) < minP2pRate) {
      toast.error(`P2P Payin Fee ต้องไม่น้อยกว่า ${minP2pRate}% (P2P rate ของคุณ)`);
      return;
    }
    if (editP2pPayoutFee !== '' && parseFloat(editP2pPayoutFee) < minP2pPayoutRate) {
      toast.error(`P2P Payout Fee ต้องไม่น้อยกว่า ${minP2pPayoutRate}% (P2P payout rate ของคุณ)`);
      return;
    }

    setSaving(true);
    try {
      await merchantApi.put(`/reseller/merchants/${merchantId}/fee`, {
        fee_percentage: feeVal,
        payout_fee_percentage: payoutFeeVal,
        p2p_fee_payin_pct: editP2pFee === '' ? null : parseFloat(editP2pFee),
        p2p_fee_payout_pct: editP2pPayoutFee === '' ? null : parseFloat(editP2pPayoutFee),
        company_name: editCompanyName
      });
      toast.success('อัปเดท Fee สำเร็จ!');
      setEditingId(null);
      loadMerchants();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSaving(false);
    }
  };

  const startEditLabels = (merchant: MerchantWithStats) => {
    setEditingLabelsId(merchant.id);
    setEditingLabels([...(merchant.reseller_labels || [])]);
    setLabelInput('');
  };

  const cancelEditLabels = () => {
    setEditingLabelsId(null);
    setLabelInput('');
  };

  const addLabel = () => {
    const trimmed = labelInput.trim();
    if (!trimmed || editingLabels.includes(trimmed) || editingLabels.length >= 10) return;
    setEditingLabels([...editingLabels, trimmed]);
    setLabelInput('');
  };

  const removeLabel = (label: string) => {
    setEditingLabels(editingLabels.filter(l => l !== label));
  };

  const saveLabels = async (merchantId: string) => {
    setSavingLabels(true);
    try {
      await merchantApi.put(`/reseller/merchants/${merchantId}/labels`, { labels: editingLabels });
      toast.success('บันทึก Labels สำเร็จ!');
      setEditingLabelsId(null);
      loadMerchants();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSavingLabels(false);
    }
  };

  const startChangePassword = (merchantId: string) => {
    setChangingPasswordId(merchantId);
    setPasswordData({ new_password: '', confirm_password: '' });
    setShowPasswords({ new_password: false, confirm: false });
  };

  const cancelChangePassword = () => {
    setChangingPasswordId(null);
    setPasswordData({ new_password: '', confirm_password: '' });
  };

  const savePassword = async (merchantId: string) => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('รหัสผ่านยืนยันไม่ตรงกัน');
      return;
    }
    setSavingPassword(true);
    try {
      await merchantApi.put(`/reseller/merchants/${merchantId}/password`, passwordData);
      toast.success('เปลี่ยนรหัสผ่านสำเร็จ!');
      setChangingPasswordId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSavingPassword(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 10) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;
    return score;
  };

  const strengthColors = ['', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
  const strengthLabels = ['', 'อ่อนมาก', 'อ่อน', 'ปานกลาง', 'ดี', 'แข็งแกร่ง'];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-12 h-12 border-4 rounded-full animate-spin"
          style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'transparent' }}
        ></div>
      </div>
    );
  }

  const labelsMerchant = merchants.find(m => m.id === editingLabelsId);
  const passwordMerchant = merchants.find(m => m.id === changingPasswordId);
  const passwordMismatch = !!passwordData.confirm_password && passwordData.confirm_password !== passwordData.new_password;

  return (
    <div>
      <UiPageHeader
        icon={faUsers}
        title="My Merchants"
        subtitle={`Merchants in your network (${merchants.length})`}
      />

      {merchants.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-12 shadow-theme text-center">
          <FontAwesomeIcon icon={faUsers} className="text-4xl text-gray-300 dark:text-slate-600 mb-4" />
          <p className="text-gray-500 dark:text-slate-400">No merchants yet</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {merchants.map((merchant) => (
            <div key={merchant.id} className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-theme hover:shadow-theme-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">{merchant.name}</h3>
                    <StatusBadge status={merchant.is_active ? 'active' : 'inactive'} statusMap={MERCHANT_STATUS_MAP} />
                    {(merchant.reseller_labels || []).map(label => (
                      <span key={label} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getLabelColor(label)}`}>
                        {label}
                      </span>
                    ))}
                  </div>
                  <p className="text-gray-500 dark:text-slate-400 text-sm">{merchant.email}</p>
                  {merchant.company_name && (
                    <p className="text-gray-400 dark:text-slate-500 text-sm">{merchant.company_name}</p>
                  )}

                  <div className="mt-2 flex flex-wrap gap-1">
                    <button onClick={() => startChangePassword(merchant.id)} className={GHOST_BTN_CLASS}>
                      <FontAwesomeIcon icon={faLock} className="mr-1" />เปลี่ยนรหัสผ่าน
                    </button>
                    <button onClick={() => startEditLabels(merchant)} className={GHOST_BTN_CLASS}>
                      <FontAwesomeIcon icon={faTags} className="mr-1" />จัดการ Labels
                    </button>
                  </div>
                </div>
                <div className="text-right ml-4">
                  {editingId === merchant.id ? (
                    <div className="flex flex-col gap-2 items-end">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 dark:text-slate-500">บริษัท:</span>
                        <input
                          type="text"
                          value={editCompanyName}
                          onChange={(e) => setEditCompanyName(e.target.value)}
                          placeholder="ชื่อบริษัท"
                          className={`w-40 ${COMPACT_INPUT_CLASS}`}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 dark:text-slate-500">P.In:</span>
                        <input
                          type="number"
                          step="0.01"
                          min={resellerUser.fee_rate || 0}
                          value={editFee}
                          onChange={(e) => setEditFee(e.target.value)}
                          className={`w-20 text-right ${COMPACT_INPUT_CLASS}`}
                        />
                        <span className="text-xs text-gray-400 dark:text-slate-500">%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 dark:text-slate-500">P.Out:</span>
                        <input
                          type="number"
                          step="0.01"
                          min={resellerUser.payout_fee_rate || 0}
                          value={editPayoutFee}
                          onChange={(e) => setEditPayoutFee(e.target.value)}
                          className={`w-20 text-right ${COMPACT_INPUT_CLASS}`}
                        />
                        <span className="text-xs text-gray-400 dark:text-slate-500">%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-indigo-400">P2P In:</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editP2pFee}
                          onChange={(e) => setEditP2pFee(e.target.value)}
                          placeholder="ปกติ"
                          className="w-20 px-2 py-1 border border-indigo-200 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <span className="text-xs text-gray-400">%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-indigo-400">P2P Out:</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editP2pPayoutFee}
                          onChange={(e) => setEditP2pPayoutFee(e.target.value)}
                          placeholder="ปกติ"
                          className="w-20 px-2 py-1 border border-indigo-200 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <span className="text-xs text-gray-400">%</span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => saveFee(merchant.id)}
                          disabled={saving}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium text-white bg-gradient-primary hover:opacity-90 disabled:opacity-50 transition-opacity"
                        >
                          <FontAwesomeIcon icon={faSave} className="mr-1" />Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="px-2.5 py-1 rounded-lg text-xs bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 transition-colors"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-slate-400">Payin Fee: {merchant.fee_percentage}%</p>
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium">P.In Commission: {parseFloat((merchant.stats.commission_rate || 0).toFixed(2))}%</p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">Payout Fee: {merchant.payout_fee_percentage || 0}%</p>
                      <p className="text-sm text-indigo-500 dark:text-indigo-400">P2P Fee: {merchant.p2p_fee_payin_pct != null ? `${merchant.p2p_fee_payin_pct}%` : 'ปกติ'} / {merchant.p2p_fee_payout_pct != null ? `${merchant.p2p_fee_payout_pct}%` : 'ปกติ'}</p>
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">P.Out Commission: {parseFloat((merchant.stats.payout_commission_rate || 0).toFixed(2))}%</p>
                      <button onClick={() => startEdit(merchant)} className={`mt-1 ${GHOST_BTN_CLASS}`}>
                        <FontAwesomeIcon icon={faEdit} className="mr-1" />ปรับ Fee
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
                <div>
                  <p className="text-xs text-gray-400 dark:text-slate-500">Paid Orders</p>
                  <p className="text-lg font-semibold text-gray-700 dark:text-slate-200 tabular-nums">{merchant.stats.total_orders.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-slate-500">Total Revenue</p>
                  <p className="text-lg font-semibold tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{formatCurrency(merchant.stats.total_paid_amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-slate-500">Payin Commission</p>
                  <p className="text-lg font-semibold text-green-600 dark:text-green-400 tabular-nums">฿{formatCurrency(merchant.stats.total_commission)}</p>
                </div>
              </div>
              {(merchant.stats.total_payout_commission || 0) > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-2 pt-2 border-t border-gray-100 dark:border-slate-700/50">
                  <div>
                    <p className="text-xs text-gray-400 dark:text-slate-500">Payouts</p>
                    <p className="text-lg font-semibold text-gray-700 dark:text-slate-200 tabular-nums">{(merchant.stats.total_payouts || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-slate-500">Payout Amount</p>
                    <p className="text-lg font-semibold tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{formatCurrency(merchant.stats.total_payout_amount || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-slate-500">Payout Commission</p>
                    <p className="text-lg font-semibold text-blue-600 dark:text-blue-400 tabular-nums">฿{formatCurrency(merchant.stats.total_payout_commission || 0)}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Labels editor modal */}
      <UiModal
        open={!!editingLabelsId}
        onClose={cancelEditLabels}
        title="แก้ไข Labels"
        icon={faTags}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        closeOnBackdrop={false}
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={cancelEditLabels} disabled={savingLabels} className="flex-1 justify-center">ยกเลิก</UiButton>
            <UiButton
              icon={faSave}
              loading={savingLabels}
              onClick={() => { if (editingLabelsId) saveLabels(editingLabelsId); }}
              className="flex-1 justify-center"
            >
              Save
            </UiButton>
          </div>
        }
      >
        <div className="space-y-3">
          {labelsMerchant && (
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">Merchant</span>
                <span className="text-gray-800 dark:text-slate-100 font-medium">{labelsMerchant.name}</span>
              </div>
            </div>
          )}
          <div>
            <p className={FIELD_LABEL_CLASS}>Labels ปัจจุบัน</p>
            <div className="flex flex-wrap gap-1.5 min-h-[28px]">
              {editingLabels.length === 0 && (
                <span className="text-xs text-gray-300 dark:text-slate-600 italic">ยังไม่มี label</span>
              )}
              {editingLabels.map(label => (
                <span key={label} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getLabelColor(label)}`}>
                  {label}
                  <button onClick={() => removeLabel(label)} className="hover:opacity-70 ml-0.5">
                    <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                  </button>
                </span>
              ))}
            </div>
          </div>
          {(() => {
            const existingLabels = Array.from(new Set(
              merchants.flatMap(m => m.reseller_labels || [])
            )).filter(l => !editingLabels.includes(l)).sort();
            return existingLabels.length > 0 ? (
              <div>
                <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-1">Labels ที่มีอยู่ — คลิกเพื่อเพิ่ม</p>
                <div className="flex flex-wrap gap-1.5">
                  {existingLabels.map(label => (
                    <button
                      key={label}
                      onClick={() => setEditingLabels(prev => [...prev, label])}
                      disabled={editingLabels.length >= 10}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-dashed opacity-60 hover:opacity-100 transition-opacity disabled:cursor-not-allowed ${getLabelColor(label)}`}
                    >
                      <FontAwesomeIcon icon={faPlus} className="text-[9px]" />{label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null;
          })()}
          <div className="flex gap-2">
            <input
              type="text"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLabel(); } }}
              placeholder="พิมพ์ label ใหม่ แล้วกด Enter"
              maxLength={30}
              className={`flex-1 ${FIELD_INPUT_CLASS}`}
            />
            <button
              type="button"
              onClick={addLabel}
              className="px-3.5 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>
      </UiModal>

      {/* Change password modal */}
      <UiModal
        open={!!changingPasswordId}
        onClose={cancelChangePassword}
        title="เปลี่ยนรหัสผ่าน"
        icon={faLock}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        closeOnBackdrop={false}
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={cancelChangePassword} disabled={savingPassword} className="flex-1 justify-center">ยกเลิก</UiButton>
            <UiButton
              icon={faSave}
              loading={savingPassword}
              disabled={!passwordData.new_password || passwordData.new_password !== passwordData.confirm_password}
              onClick={() => { if (changingPasswordId) savePassword(changingPasswordId); }}
              className="flex-1 justify-center"
            >
              บันทึก
            </UiButton>
          </div>
        }
      >
        <div className="space-y-3">
          {passwordMerchant && (
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">Merchant</span>
                <span className="text-gray-800 dark:text-slate-100 font-medium">{passwordMerchant.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">Email</span>
                <span className="text-gray-800 dark:text-slate-100">{passwordMerchant.email}</span>
              </div>
            </div>
          )}
          <div className="relative">
            <input
              type={showPasswords.new_password ? 'text' : 'password'}
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
              placeholder="รหัสผ่านใหม่ (อย่างน้อย 10 ตัว + ตัวเลข + อักขระพิเศษ)"
              className={`${FIELD_INPUT_CLASS} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPasswords(p => ({ ...p, new_password: !p.new_password }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
            >
              <FontAwesomeIcon icon={showPasswords.new_password ? faEyeSlash : faEye} className="text-xs" />
            </button>
          </div>
          {passwordData.new_password && (
            <div>
              <div className="flex gap-1 mb-0.5">
                {[1, 2, 3, 4, 5].map(i => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      i <= getPasswordStrength(passwordData.new_password)
                        ? strengthColors[getPasswordStrength(passwordData.new_password)]
                        : 'bg-gray-200 dark:bg-slate-600'
                    }`}
                  />
                ))}
              </div>
              <p className="text-[10px] text-gray-400 dark:text-slate-500">ความปลอดภัย: {strengthLabels[getPasswordStrength(passwordData.new_password)]}</p>
            </div>
          )}
          <div className="relative">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              placeholder="ยืนยันรหัสผ่านใหม่"
              className={`w-full px-3.5 py-2.5 rounded-lg text-sm pr-10 border transition-colors focus:outline-none focus:ring-2 text-gray-800 dark:text-slate-200 placeholder:text-gray-400 dark:placeholder:text-slate-500 ${
                passwordMismatch
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 focus:border-red-400 focus:ring-red-100 dark:focus:ring-red-900/40'
                  : 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
            >
              <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} className="text-xs" />
            </button>
          </div>
          {passwordMismatch && (
            <p className="text-[10px] text-red-500 dark:text-red-400">รหัสผ่านไม่ตรงกัน</p>
          )}
        </div>
      </UiModal>
    </div>
  );
};

export default ResellerMerchantsPage;
